/**
 * Lookup match card.
 *
 * Renders the resolved subject from GET /v1/admin/lookup: account, user, and
 * entitlement legs (each independently nullable per the contract — a user-only
 * match, for example, renders without the account row). No PII exists in the
 * response by contract; only ids, statuses, and the plan snapshot.
 */
import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { CopyButton } from '@/components/CopyButton';
import { ArrowRight } from '@/components/icons';
import type { AdminLookupMatch } from '@/api/admin-types';
import {
  presentEffectiveState,
  presentEntitlementSource,
  presentLifecycleStatus,
} from '@/features/account/AccountFormatters';

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex flex-wrap items-center gap-2 text-sm">
      <dt className="w-24 shrink-0 text-muted-foreground">{label}</dt>
      <dd className="flex min-w-0 flex-wrap items-center gap-2">{value}</dd>
    </div>
  );
}

export function LookupMatchCard({ match }: { match: AdminLookupMatch }) {
  const { account, user, entitlement } = match;

  return (
    <Card data-testid="lookup-match-card">
      <CardHeader>
        <CardTitle className="flex flex-wrap items-center gap-2 text-base">
          <span>{account ? account.name : user ? `User ${user.id}` : 'Subject'}</span>
          {account ? (
            <Badge variant={presentLifecycleStatus(account.status).variant}>
              {presentLifecycleStatus(account.status).label}
            </Badge>
          ) : null}
        </CardTitle>
        <CardDescription>
          {account ? 'Resolved account, user, and entitlement snapshot.' : 'Resolved user only — no account linked.'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {account ? (
          <dl className="space-y-2">
            <Row
              label="Account"
              value={
                <>
                  <span className="font-mono text-xs" data-testid="lookup-match-account-id">
                    {account.id}
                  </span>
                  <CopyButton value={account.id} label="Copy account id" />
                </>
              }
            />
          </dl>
        ) : null}

        {user ? (
          <dl className="space-y-2 border-t pt-4">
            <Row
              label="User"
              value={
                <>
                  <span className="font-mono text-xs">{user.clerk_user_id}</span>
                  <Badge variant={presentLifecycleStatus(user.status).variant}>
                    {presentLifecycleStatus(user.status).label}
                  </Badge>
                  <CopyButton value={user.clerk_user_id} label="Copy Clerk user id" />
                </>
              }
            />
          </dl>
        ) : (
          <p className="border-t pt-4 text-sm text-muted-foreground" data-testid="lookup-match-no-user">
            No linked user for this account.
          </p>
        )}

        <div className="border-t pt-4">
          {entitlement ? (
            <dl className="space-y-2">
              <Row
                label="Entitlement"
                value={
                  <>
                    <Badge variant="secondary">{entitlement.plan}</Badge>
                    <Badge variant={presentEntitlementSource(entitlement.source).variant}>
                      {presentEntitlementSource(entitlement.source).label}
                    </Badge>
                    <Badge variant={presentEffectiveState(entitlement).variant}>
                      {presentEffectiveState(entitlement).label}
                    </Badge>
                  </>
                }
              />
            </dl>
          ) : (
            <p className="text-sm text-muted-foreground" data-testid="lookup-match-no-entitlement">
              No entitlement snapshot on file.
            </p>
          )}
        </div>

        {account ? (
          <div className="border-t pt-4">
            {/* The primary continuation: open the full account surface. */}
            <Button asChild data-testid="lookup-open-account">
              <Link to={`/accounts/${account.id}`}>
                Open account
                <ArrowRight aria-hidden="true" />
              </Link>
            </Button>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
